import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// Format patterns for time with and without seconds
const TIME_FORMAT_WITH_SECONDS = 'HH:mm:ss';
const TIME_FORMAT_WITHOUT_SECONDS = 'HH:mm';

export function validateDate(date: Date | undefined | null): boolean {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return false;
  }
  return true;
}

/**
 * Helper function to modify format string based on hideSeconds flag
 * @param formatString The original format string
 * @param hideSeconds Whether to hide seconds in time display
 * @returns Modified format string with appropriate time format
 */
export const getAdjustedFormatString = (
  formatString: string,
  hideSeconds = false,
): string => {
  // If format string contains time format with seconds
  if (formatString.includes(TIME_FORMAT_WITH_SECONDS) && hideSeconds) {
    return formatString.replace(
      TIME_FORMAT_WITH_SECONDS,
      TIME_FORMAT_WITHOUT_SECONDS,
    );
  }

  return formatString;
};

export const formatDateTime = (
  date: Date | undefined | null,
  formatString: string,
  hideSeconds = false,
): string => {
  if (!date) {
    return '';
  }

  if (!validateDate(date)) {
    console.error('Invalid date:', date);
    return '';
  }

  // Adjust the format string based on hideSeconds flag
  const adjustedFormatString = getAdjustedFormatString(
    formatString,
    hideSeconds,
  );

  return format(date, adjustedFormatString);
};

export const formatDate = (
  date: Date | undefined | null,
  formatString: string,
): string => {
  const dateTimeString = formatDateTime(date, formatString);

  if (!dateTimeString) {
    return dateTimeString;
  }

  // Extract only the date part from the formatted string
  const [datePart] = dateTimeString.split(' ');

  return datePart;
};

export const formatDateTimeWithTimeZone = (
  date: Date | undefined | null,
  timeZone: string,
  formatString: string,
  hideSeconds = false,
) => {
  if (!date) {
    return '';
  }

  if (!validateDate(date)) {
    console.error('Invalid date:', date);
    return '';
  }

  // Adjust the format string based on hideSeconds flag
  const adjustedFormatString = getAdjustedFormatString(
    formatString,
    hideSeconds,
  );

  return formatInTimeZone(new Date(date), timeZone, adjustedFormatString);
};

export const formatDateWithTimeZone = (
  date: Date | undefined | null,
  timeZone: string,
  formatString: string,
): string => {
  const dateTimeString = formatDateTimeWithTimeZone(
    date,
    timeZone,
    formatString,
  );

  if (!dateTimeString) {
    return dateTimeString;
  }

  // Extract only the date part from the formatted string
  const [datePart] = dateTimeString.split(' ');

  return datePart;
};

export const formatTimeWithTimeZone = (
  date: Date | undefined | null,
  timeZone: string,
  formatString: string,
  hideSeconds = false,
): string => {
  const dateTimeString = formatDateTimeWithTimeZone(
    date,
    timeZone,
    formatString,
    hideSeconds,
  );

  if (!dateTimeString) {
    return dateTimeString;
  }

  // Extract only the time part from the formatted string
  const parts = dateTimeString.split(' ');
  const timePart = parts.length > 1 ? parts[parts.length - 1] : '';

  return timePart;
};
